// Tag-name uniqueness without the name (spec section 6.5):
//   nameIndex = base64url( HMAC-SHA256( blindIndexKey, NFKC(trim(lower(name))) ) )
// The key derives from MK, so a database reader cannot dictionary-attack tag
// names, and a rotation changes every personal tag's index. The server
// compares indexes within (creatorId, programSlug) and never sees a name.
import { bytesToBase64Url, utf8Encode } from './encoding';
import { assertMemberId } from './identifiers';
import { assertMasterKey } from './master-key';
import { getSubtle, hkdfKey } from './webcrypto';

/** The shape the API validates: a 32-byte digest in base64url, no padding. */
export const BLIND_INDEX_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const INDEX_INFO = 'tag-index';

/**
 * @param {string} name
 * @returns {string} NFKC(trim(lower(name))), the exact order the contract states
 */
export function normaliseTagName(name) {
  if (typeof name !== 'string') throw new TypeError('name must be a string');
  const normalised = name.toLowerCase().trim().normalize('NFKC');
  if (!normalised) throw new TypeError('name must not be empty');
  return normalised;
}

/**
 * HKDF-SHA256(MK, salt = memberId, info = "tag-index") as an HMAC key.
 *
 * @param {Uint8Array} mk
 * @param {number} memberId
 * @returns {Promise<CryptoKey>}
 */
function deriveBlindIndexKey(mk, memberId) {
  assertMasterKey(mk);
  assertMemberId(memberId);
  return hkdfKey({
    ikm: mk,
    salt: utf8Encode(String(memberId)),
    info: INDEX_INFO,
    algorithm: { name: 'HMAC', hash: 'SHA-256', length: 256 },
    usages: ['sign'],
  });
}

/**
 * @param {object} args
 * @param {Uint8Array} args.mk
 * @param {number} args.memberId
 * @param {string} args.name the tag name as the member typed it
 * @returns {Promise<string>} the tag's `nameIndex`
 */
export async function tagNameIndex({ mk, memberId, name }) {
  const normalised = normaliseTagName(name);
  const key = await deriveBlindIndexKey(mk, memberId);
  const mac = await getSubtle().sign('HMAC', key, utf8Encode(normalised));
  return bytesToBase64Url(new Uint8Array(mac));
}
